/**
 * Authentication utility functions
 */

/**
 * Extracts error message from various error formats
 * @param {Error|Object} error - Error object from API or exception
 * @param {string} defaultMessage - Default message if none found
 * @returns {string} Error message
 */
export const extractErrorMessage = (error, defaultMessage = "An error occurred") => {
  if (!error) return defaultMessage;

  // Handle axios error response
  if (error.response) {
    return (
      error.response.data?.message ||
      error.response.data?.error ||
      error.response.statusText ||
      defaultMessage
    );
  }

  // Handle error object with message
  if (error.message) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
};

/**
 * Returns true only for safe, internal application paths.
 * Rejects absolute URLs (http://evil.com), protocol-relative URLs (//evil.com),
 * and anything that isn't anchored to the app root, to prevent open redirects.
 */
const isSafeInternalPath = (path) => {
  if (typeof path !== "string" || path.length === 0) return false;
  // Must start with a single "/" and not be protocol-relative ("//").
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  // Reject control characters and backslashes that browsers may normalize.
  if (/[\\\x00-\x1f]/.test(path)) return false;
  // Reject embedded scheme (e.g. "/redirect?next=http://evil").
  if (/^\/?[a-z][a-z0-9+.-]*:/i.test(path)) return false;
  return true;
};

/**
 * Normalizes React Router location.state.from to a safe internal path string.
 * Supports both string and { pathname, search } object shapes. Returns "" for
 * any value that isn't a safe internal path.
 */
export const resolveNavigationFromPath = (from) => {
  if (!from) return "";
  let candidate = "";
  if (typeof from === "string") {
    candidate = from;
  } else if (typeof from === "object" && from.pathname) {
    candidate = `${from.pathname}${from.search || ""}`;
  }
  return isSafeInternalPath(candidate) ? candidate : "";
};

/**
 * Determines redirect path from location state or default
 * @param {Object} location - React Router location object
 * @param {string} defaultPath - Default redirect path
 * @returns {string} Redirect path
 */
export const getRedirectPath = (location, defaultPath = "/") => {
  return resolveNavigationFromPath(location?.state?.from) || defaultPath;
};

/**
 * Standardizes login result handling
 * @param {boolean|Object} result - Login result from context
 * @returns {{success: boolean, message: string, user: any}}
 */
export const normalizeLoginResult = (result) => {
  if (typeof result === "boolean") {
    return {
      success: result,
      message: result ? "" : "Invalid email or password",
      user: null,
    };
  }

  if (result && typeof result === "object") {
    const success = result.ok !== false && result.ok !== undefined ? result.ok : !!result.user;
    return {
      success,
      message: success ? (result.message || "") : (result.message || "Invalid email or password"),
      user: result.user || null,
    };
  }

  return {
    success: false,
    message: "Invalid login response",
    user: null,
  };
};
